/**
 * Admin page — system administration and HR sync controls.
 * Route: /admin
 * Access: ADMIN only
 */

import { useState, useEffect } from "react";
import {
  RefreshCw,
  Users,
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Button, Spinner } from "../../components/ui/index.jsx";
import { formatRelative } from "../../utils/format";
import apiClient from "../../api/client";

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------
function useSyncStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/api/admin/hr/sync/status");
      setStatus(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return { status, loading, refresh };
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------
function SyncStatusBadge({ success, label }) {
  if (label === "never_run") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
        <Clock className="h-3.5 w-3.5" /> Never synced
      </span>
    );
  }
  return success ? (
    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600">
      <CheckCircle2 className="h-3.5 w-3.5" /> Success
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-xs text-rose-600">
      <XCircle className="h-3.5 w-3.5" /> Failed
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sync card
// ---------------------------------------------------------------------------
function SyncCard({
  title,
  icon: Icon,
  description,
  syncStatus,
  onSync,
  syncing,
}) {
  const [expanded, setExpanded] = useState(false);
  const isNever = syncStatus?.status === "never_run";

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
              <Icon className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">{title}</p>
              <p className="text-sm text-slate-500 mt-0.5">{description}</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={onSync}
            isLoading={syncing}
            className="shrink-0"
          >
            {!syncing && <RefreshCw className="h-4 w-4" />}
            Sync Now
          </Button>
        </div>

        {/* Last sync summary */}
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm">
            {!isNever && (
              <>
                <SyncStatusBadge success={syncStatus?.success} />
                <span className="text-slate-400 text-xs">
                  {syncStatus?.completedAt
                    ? new Date(syncStatus.completedAt).toLocaleString("id-ID", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "—"}
                </span>
                {syncStatus?.employeeCount != null && (
                  <span className="text-slate-400 text-xs">
                    {syncStatus.employeeCount} records
                  </span>
                )}
                {syncStatus?.entityCount != null && (
                  <span className="text-slate-400 text-xs">
                    {syncStatus.entityCount} records
                  </span>
                )}
              </>
            )}
            {isNever && <SyncStatusBadge label="never_run" />}
          </div>
          {!isNever && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
            >
              Details{" "}
              {expanded ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </button>
          )}
        </div>

        {/* Expanded details */}
        {expanded && syncStatus && !isNever && (
          <div className="mt-3 bg-slate-50 rounded-lg p-3 text-xs text-slate-600 space-y-1 font-mono">
            {Object.entries(syncStatus)
              .filter(([k]) => !["status"].includes(k))
              .map(([k, v]) => (
                <div key={k} className="flex gap-3">
                  <span className="text-slate-400 w-32 shrink-0">{k}</span>
                  <span>
                    {typeof v === "boolean"
                      ? v
                        ? "true"
                        : "false"
                      : String(v ?? "—")}
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sync log
// ---------------------------------------------------------------------------
function SyncLog({ entries }) {
  if (!entries.length) return null;
  return (
    <div className="mt-6">
      <h2 className="text-sm font-semibold text-slate-700 mb-3">
        Session Activity
      </h2>
      <div className="space-y-2">
        {entries.map((entry, i) => (
          <div
            key={i}
            className={`flex items-start gap-3 rounded-lg px-4 py-3 text-sm ${
              entry.type === "success"
                ? "bg-emerald-50 text-emerald-800"
                : entry.type === "error"
                  ? "bg-rose-50 text-rose-800"
                  : "bg-slate-50 text-slate-700"
            }`}
          >
            {entry.type === "success" && (
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            )}
            {entry.type === "error" && (
              <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
            )}
            {entry.type === "info" && (
              <Clock className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
            )}
            <div className="flex-1">
              <p>{entry.message}</p>
              {entry.detail && (
                <p className="text-xs opacity-70 mt-0.5">{entry.detail}</p>
              )}
            </div>
            <span className="text-xs opacity-50 shrink-0">{entry.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AdminPage() {
  const { user } = useAuth();
  const {
    status,
    loading: statusLoading,
    refresh: refreshStatus,
  } = useSyncStatus();
  const [syncing, setSyncing] = useState({
    entities: false,
    employees: false,
    all: false,
  });
  const [log, setLog] = useState([]);

  // Auto-fetch status on mount
  useEffect(() => {
    refreshStatus();
  }, []);

  function addLog(type, message, detail) {
    const time = new Date().toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    setLog((l) => [{ type, message, detail, time }, ...l].slice(0, 20));
  }

  async function runSync(resource) {
    setSyncing((s) => ({ ...s, [resource]: true }));
    addLog("info", `Starting ${resource} sync...`);
    try {
      const { data } = await apiClient.post("/api/admin/hr/sync", { resource });
      if (resource === "entities" || resource === "all") {
        const e = data.entities;
        if (e?.success) {
          addLog(
            "success",
            `Entities synced`,
            `${e.entityCount} entities · ${e.deactivatedCount} deactivated`,
          );
        } else if (e) {
          addLog("error", `Entity sync failed`, e.error || e.reason);
        }
      }
      if (resource === "employees" || resource === "all") {
        const e = data.employees;
        if (e?.success) {
          addLog(
            "success",
            `Employees synced`,
            `${e.employeeCount} employees · ${e.userCreated} accounts created · ${e.userUpdated} updated`,
          );
        } else if (e) {
          addLog("error", `Employee sync failed`, e.error || e.reason);
        }
      }
      await refreshStatus();
    } catch (err) {
      addLog(
        "error",
        `Sync request failed`,
        err.response?.data?.error || err.message,
      );
    } finally {
      setSyncing((s) => ({ ...s, [resource]: false }));
    }
  }

  async function handleForceFullSync() {
    if (
      !confirm(
        "This clears sync timestamps and forces a full re-sync of all data from HR. Continue?",
      )
    )
      return;
    setSyncing((s) => ({ ...s, all: true }));
    addLog("info", "Clearing sync timestamps for full re-sync...");
    try {
      // Clear timestamps via a special admin endpoint (or just run sync — delta will still work)
      await runSync("all");
    } finally {
      setSyncing((s) => ({ ...s, all: false }));
    }
  }

  if (user?.role !== "ADMIN") {
    return (
      <div className="text-center py-20">
        <AlertTriangle className="h-10 w-10 text-amber-300 mx-auto mb-3" />
        <p className="text-slate-600 font-medium">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Admin</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            System administration and HR sync controls
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={refreshStatus}
          isLoading={statusLoading}
        >
          <RefreshCw className="h-4 w-4" /> Refresh Status
        </Button>
      </div>

      {/* HR Sync section */}
      <div className="space-y-4">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          HR Sync
        </h2>

        <SyncCard
          title="Entity Sync"
          icon={Building2}
          description="Sync companies and entities from HR. Run this first if entities are missing."
          syncStatus={status?.entities}
          onSync={() => runSync("entities")}
          syncing={syncing.entities}
        />

        <SyncCard
          title="Employee Sync"
          icon={Users}
          description="Sync employees from HR and auto-provision inventory user accounts. New employees get VIEWER access scoped to their entity."
          syncStatus={status?.employees}
          onSync={() => runSync("employees")}
          syncing={syncing.employees}
        />

        {/* Sync all */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-900">Sync All</p>
              <p className="text-sm text-slate-500 mt-0.5">
                Run entity sync then employee sync in one go. Recommended for
                initial setup.
              </p>
            </div>
            <Button
              onClick={() => runSync("all")}
              isLoading={syncing.all}
              className="shrink-0"
            >
              {!syncing.all && <RefreshCw className="h-4 w-4" />}
              Sync All
            </Button>
          </div>
        </div>

        {/* Cron status */}
        <div className="bg-slate-50 rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-3 text-sm text-slate-600">
          <Clock className="h-4 w-4 text-slate-400 shrink-0" />
          <span>Automatic sync runs hourly at :05 via background cron.</span>
        </div>
      </div>

      {/* Session log */}
      <SyncLog entries={log} />
    </div>
  );
}
